import './promo-tiles.scss'
import { useQuery } from '@tanstack/react-query'
import { minutesToMilliseconds } from 'date-fns'
import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { OffersAndCompetitionsSummaryModal } from '@/content/components/offersaAndCompetitionsSummaryModal/OffersAndCompetitionsSummaryModal'
import { OffersAndCompetitionsConfigurationPropsApi } from '@/content/components/offersAndCompetitions/OffersAndCompetitions.models'
import { cmsService } from '../../../../content/services/cms.service'
import { navigationService } from '../../../../content/services/navigation.service'
import type { CmsPromoItem, OffersConfigWrapper, PromoTilesProps, PromoTileType, PromoType } from './types'
import { getIconByPromoType } from './utils/getIconByPromoType'

export const PromoTiles: FC<PromoTilesProps> = ({ filmId }) => {
    const [tiles, setTiles] = useState<PromoTileType[]>([])
    const [showModal, setShowModal] = useState(false)
    const [selectedTile, setSelectedTile] = useState<PromoTileType | null>(null)

    const { data: configWrapper } = useQuery({
        queryKey: ['offers-competitions-configuration'],
        staleTime: minutesToMilliseconds(60),
        queryFn: async () =>
            cmsService.loadConfigurationComponents<OffersConfigWrapper>({
                data: { OffersAndCompetitions: new OffersAndCompetitionsConfigurationPropsApi() },
                friendlyUrl: '/',
            }),
    })

    const modalButtonLabel = configWrapper?.OffersAndCompetitions?.modalButtonLabel || 'View Offer'

    useEffect(() => {
        if (!filmId) return

        void cmsService
            .loadPageList<CmsPromoItem>(
                [
                    'nodeId',
                    'promoTileTitle',
                    'promoTileMessage',
                    'urlSegment',
                    'relatedFilms',
                    'type',
                    'heroBanner',
                    'pageTitle',
                    'introText',
                    'endDate',
                ],
                navigationService.getPageUrl('OffersAndCompetitionsLanding')
            )
            .then(list => {
                const now = new Date()
                const filtered = list
                    .filter(item => item?.relatedFilms?.filmPicker?.options?.includes(filmId))
                    .filter(item => {
                        if (!item.endDate) return true
                        const endDate = new Date(item.endDate)
                        return endDate >= now
                    })

                setTiles(
                    filtered.map(item => ({
                        id: String(item.nodeId),
                        title: item.promoTileTitle || '',
                        message: item.promoTileMessage || '',
                        type: (item.type as PromoType) || undefined,
                        urlSegment: item.urlSegment,
                        // Modal data
                        heroBanner: item.heroBanner,
                        modalPageTitle: item.pageTitle,
                        introText: item.introText,
                    }))
                )
            })
    }, [filmId])

    const handleTileClick = (tile: PromoTileType) => {
        setSelectedTile(tile)
        setShowModal(true)
    }

    const handleModalClose = () => {
        setShowModal(false)
        setSelectedTile(null)
    }

    const handleButtonClick = () => {
        if (selectedTile) {
            navigationService.goToPage('OfferPage', { offerName: selectedTile.urlSegment })
        }
        setShowModal(false)
        setSelectedTile(null)
    }

    const getHeaderImage = (tile: PromoTileType) => {
        if (!tile.heroBanner?.desktop) return undefined

        return {
            url: tile.heroBanner.desktop,
            alt: tile.modalPageTitle || '',
            fromLeft: 50,
            fromTop: 50,
        }
    }

    if (!tiles.length) return null

    return (
        <>
            <div className="v-promo-tiles" data-chromatic="promo-tiles">
                {tiles.map(tile => {
                    const { src, alt } = getIconByPromoType(tile.type)
                    return (
                        <div onClick={() => handleTileClick(tile)} key={tile.id} className="promo-tile">
                            <div className="promo-tile__icon-square">
                                <img src={src} alt={alt} width={32} height={32} />
                            </div>
                            <div className="promo-tile__text">
                                <div className="promo-tile__title">{tile.title}</div>
                                <div className="promo-tile__message">{tile.message}</div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {selectedTile && (
                <OffersAndCompetitionsSummaryModal
                    show={showModal}
                    headerImage={getHeaderImage(selectedTile)}
                    modalTitle={selectedTile.modalPageTitle}
                    modalContent={selectedTile.introText || ''}
                    closeButtonLabel={modalButtonLabel}
                    onClose={handleModalClose}
                    onButtonClick={handleButtonClick}
                />
            )}
        </>
    )
}
